import React from 'react';
import { Box, Typography, Paper, useTheme, alpha } from '@mui/material';
import { Inbox as InboxIcon } from '@mui/icons-material';
import { Droppable } from '@hello-pangea/dnd';

// Helper function to determine if a color is light or dark
const isLightColor = (color: string): boolean => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  }

  // Handle common color names
  const lightColors = [
    'white',
    'yellow',
    'lime',
    'cyan',
    'lightblue',
    'lightgreen',
    'lightyellow',
    'lightcyan',
    'lightgray',
    'lightgrey',
  ];
  return lightColors.includes(color.toLowerCase());
};

const getTextColor = (backgroundColor: string): string => {
  return isLightColor(backgroundColor) ? '#1a1a1a' : 'white';
};

const formatValue = (value: number | string | undefined, includeSymbol: boolean = true): string => {
  if (value === undefined || value === null) return includeSymbol ? '$0' : '0';
  
  // If it's already a string
  if (typeof value === 'string') {
    // Remove existing $ if we don't want the symbol
    const cleanValue = value.replace(/^\$/, '');
    
    // If it's a string number, parse it and format
    const parsed = parseFloat(cleanValue.replace(/[,$]/g, ''));
    if (!isNaN(parsed)) {
      const formatted = parsed.toLocaleString();
      return includeSymbol ? `$${formatted}` : formatted;
    }
    return includeSymbol ? `$${cleanValue}` : cleanValue;
  }
  
  // If it's a number, format normally
  const formattedNumber = value.toLocaleString();
  return includeSymbol ? `$${formattedNumber}` : formattedNumber;
};

export interface KanbanColumnData {
  id: string;
  name: string;
  color: string;
}

export interface KanbanItem {
  id: string;
  [key: string]: any;
}

interface KanbanColumnProps {
  column: KanbanColumnData;
  items: KanbanItem[];
  total?: number;
  itemCountLabel?: string;
  renderCard: (item: KanbanItem, index: number) => React.ReactNode;
  emptyStateMessage?: string;
  emptyStateSubtext?: string;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  items = [],
  total,
  itemCountLabel = 'items',
  renderCard,
  emptyStateMessage = 'No items yet',
  emptyStateSubtext = 'Drag items here',
}) => {
  const theme = useTheme();
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <Paper
      sx={{
        minWidth: 320,
        maxWidth: 320,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        border: 'none',
        borderRadius: '12px',
        height: 'fit-content',
        minHeight: 400,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2.5,
          backgroundColor: column.color,
          color: getTextColor(column.color),
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            fontSize: '1.1rem', 
            mb: 0.5,
            letterSpacing: '-0.01em',
          }}
        >
          {column.name}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              opacity: 0.85, 
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {safeItems.length} {safeItems.length === 1 
              ? (itemCountLabel === 'opportunities' ? 'opportunity' : itemCountLabel.slice(0, -1))
              : itemCountLabel}
          </Typography>
          {total !== undefined && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                fontSize: '1rem',
                letterSpacing: '-0.02em',
              }}
            >
              {formatValue(total)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Droppable Content Area */}
      <Droppable droppableId={String(column.id)}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              gap: 1.5,
              backgroundColor: snapshot.isDraggingOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              transition: 'background-color 0.2s ease',
              minHeight: 300,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(0,0,0,0.2)',
                },
              },
            }}
          >
            {safeItems.map((item, index) => renderCard(item, index))}
            {provided.placeholder}
            
            {/* Empty State */}
            {safeItems.length === 0 && (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  color: 'text.secondary',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  minHeight: 200,
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  border: '2px dashed rgba(0,0,0,0.08)',
                }}
              >
                <InboxIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem', opacity: 0.6, fontWeight: 500 }}>
                  {emptyStateMessage}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', opacity: 0.5 }}>
                  {emptyStateSubtext}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
};